import { EndPoints, httpService } from "@/Core/Client";
import { AuthLoginDto, AuthOtpDto, AuthResponseDto } from "@/Dtos/auth-dto";
import { UserResponseDto } from "@/Dtos/user-dto";
import { BadRequestError } from "@Interfaces/IErrors";
import axios from "axios";
import CryptoJS from "crypto-js";
import toast from "react-hot-toast";
import { create } from "zustand";
import { StateStorage, createJSONStorage, persist } from "zustand/middleware";

/**
 * An implementation of the `StateStorage` interface that uses AES encryption
 * to securely store and retrieve items from the local storage.
 */
const EncryptedStorage: StateStorage = {
    /**
     * Retrieves an item from local storage and decrypts it.
     *
     * @param name - The name of the item to retrieve.
     * @returns A promise that resolves to the decrypted value of the item, or null if the item does not exist.
     */
    getItem: async (name: string): Promise<string | null> => {
        const enKey = import.meta.env.VITE_storage_encrypt_key;
        const value = localStorage.getItem(name);

        if (!value) return null;

        if (value) {
            const decryptedBytes = CryptoJS.AES.decrypt(value, enKey);
            const decryptedValue = decryptedBytes.toString(CryptoJS.enc.Utf8);

            return decryptedValue;
        }

        return value;
    },

    /**
     * Encrypts an item and stores it in local storage.
     *
     * @param name - The name of the item to store.
     * @param value - The value of the item to store.
     * @returns A promise that resolves when the item has been stored.
     */
    setItem: async (name: string, value: string): Promise<void> => {
        const enKey = import.meta.env.VITE_storage_encrypt_key;

        const encrypted = CryptoJS.AES.encrypt(value, enKey).toString();
        localStorage.setItem(name, encrypted);
    },

    /**
     * Removes an item from local storage.
     *
     * @param name - The name of the item to remove.
     * @returns A promise that resolves when the item has been removed.
     */
    removeItem: async (name: string): Promise<void> => {
        localStorage.removeItem(name);
    },
};

// Actions and State Together
export interface IAuthStoreState {
    isAuthorized: boolean;
    // isVerified: boolean;
    isLoading: boolean;
    auth: AuthResponseDto | undefined;
    Error: BadRequestError<AuthLoginDto> | undefined;

    updateUser: (user: UserResponseDto) => void;
    // updateRole: (user: RoleResponseDto) => void;
    login: (user: AuthLoginDto) => void;
    getAuth: () => void;
    verifyOtp: (user: AuthOtpDto) => void;
    logOut: () => void;
    ErrorClear: () => void;
}

const useAuthStore = create<IAuthStoreState>()(
    persist(
        (set, get) => ({
            isAuthorized: false,
            isLoading: false,
            // isVerified: false,
            auth: undefined,
            Error: {},
            login: async (user) => {
                try {
                    set(() => ({
                        isAuthorized: false,
                        // isVerified: false,
                        isLoading: true,
                    }));

                    const { data, status } = await httpService(
                        EndPoints.auth.login,
                    ).post(user);

                    if (status === 200) {
                        set({
                            isAuthorized: true,
                            // isVerified: false,
                            isLoading: false,
                            auth: data as AuthResponseDto,
                            Error: undefined,
                        });

                        // CustomToast({
                        //   title: `Welcome To ${import.meta.env.VITE_APP_FULL_NAME}`,
                        //   isLoading: false,
                        //   type: "success",
                        // });
                    }
                } catch (error) {
                    console.log("Login ....4", error);
                    set(() => ({
                        isAuthorized: false,
                        // isVerified: false,
                        isLoading: false,
                    }));

                    if (axios.isAxiosError(error)) {
                        const { response } = error;

                        set((s) => ({
                            ...s,
                            auth: undefined,
                            Error:
                                (response?.data as BadRequestError<AuthLoginDto>) ??
                                {},
                        }));
                    }
                }
            },

            verifyOtp: async (record) => {
                try {
                    const { data, status } = await httpService(
                        EndPoints.auth.otp,
                    ).post(record);

                    if (status === 200) {
                        const auth = data as AuthResponseDto;

                        set((s) => ({
                            ...s,
                            isAuthorized: true,
                            isLoading: false,
                            // isVerified: IsNullOrWhiteSpace(auth.accessToken)
                            //     ? false
                            //     : true,
                            auth: auth,
                            Error: undefined,
                        }));

                        toast(
                            `Welcome To ${import.meta.env.VITE_APP_FULL_NAME}`,
                            {
                                icon: "👏",
                            },
                        );
                    }
                } catch (error) {
                    set((s) => ({
                        ...s,
                        auth: s.auth,
                        isAuthorized: true,
                        // isVerified: false,
                        isLoading: false,
                    }));

                    if (axios.isAxiosError(error)) {
                        const { response } = error;

                        set((s) => ({
                            ...s,
                            auth: s.auth,
                            Error:
                                (response?.data as BadRequestError<AuthLoginDto>) ??
                                {},
                        }));
                    }
                }
            },

            updateUser: async (user) => {
                set({
                    isAuthorized: true,
                    // isVerified: true,
                    auth: {
                        user: user,
                        accessToken: get().auth!.accessToken,
                        refreshToken: get().auth!.refreshToken,
                        refreshTokenExpireTime:
                            get().auth!.refreshTokenExpireTime,
                    },
                    Error: undefined,
                });
            },

            // updateRole: async (role) => {
            //     set({
            //         isAuthorized: true,
            //         // isVerified: true,
            //         auth: {
            //             user: {
            //                 ...get().auth!.user!,
            //                 role: role,
            //             },
            //             accessToken: get().auth!.accessToken,
            //         },
            //         Error: undefined,
            //     });
            // },

            logOut: () => {
                set(() => ({
                    isAuthorized: false,
                    isLoading: false,
                    // isVerified: false,
                    auth: undefined,
                    Error: {},
                }));
            },
            getAuth: () => {
                // set(() => ({
                //     isAuthorized: false,
                //     isLoading: false,
                //     isVerified: false,
                //     auth: undefined,
                //     Error: {},
                // }));

                return get().auth;
            },

            ErrorClear: () => set(() => ({ Error: undefined })),
        }),
        {
            name: "hami-auth",
            version: 101,
            storage: createJSONStorage(() => EncryptedStorage),
        },
    ),
);

export default useAuthStore;
